import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthActions, authFeature } from '../../features/auth/state';
import {
  canAccessBackofficeProfile,
  getDefaultBackofficeRouteForRoles,
} from '../auth/role-access.util';
import { readJwtExpiry } from '../auth/jwt-expiry.util';
import { AuthSessionSyncService } from '../services/auth-session-sync.service';
import { TokenRefreshService } from '../services/token-refresh.service';

export const authGuard: CanActivateFn = (_route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const sessionSync = inject(AuthSessionSyncService);

  const token = store.selectSignal(authFeature.selectToken);
  const legacyRole = store.selectSignal(authFeature.selectRole);
  const userType = store.selectSignal(authFeature.selectUserType);
  const roles = store.selectSignal(authFeature.selectRoles);
  const permissions = store.selectSignal(authFeature.selectPermissions);

  const session = sessionSync.readSession();
  const activeToken = token() ?? session.token;
  const activeLegacyRole = legacyRole() ?? session.role;
  const profile = {
    userType: userType() ?? session.userType,
    roles: roles().length ? roles() : session.roles,
    permissions: permissions().length ? permissions() : session.permissions,
  };

  if (activeToken && canAccessBackofficeProfile(profile, activeLegacyRole)) {
    if (!token()) {
      store.dispatch(AuthActions.initSuccess(session));
    }

    return renewExpiredToken(activeToken, state.url, router, inject(TokenRefreshService));
  }

  return router.createUrlTree(['/login']);
};

function renewExpiredToken(
  jwt: string,
  returnUrl: string,
  router: Router,
  refresh: TokenRefreshService,
): boolean | Observable<boolean | UrlTree> {
  const expiry = readJwtExpiry(jwt);

  if (expiry === null || expiry > Date.now()) {
    return true;
  }

  return refresh.renew().pipe(
    map((outcome) =>
      outcome.status === 'session-ended'
        ? router.createUrlTree(['/login'], { queryParams: { returnUrl } })
        : true,
    ),
    catchError(() => of(true)),
  );
}

export const noAuthGuard: CanActivateFn = () => {
  const store = inject(Store);
  const router = inject(Router);
  const sessionSync = inject(AuthSessionSyncService);

  const token = store.selectSignal(authFeature.selectToken);
  const legacyRole = store.selectSignal(authFeature.selectRole);
  const userType = store.selectSignal(authFeature.selectUserType);
  const roles = store.selectSignal(authFeature.selectRoles);
  const permissions = store.selectSignal(authFeature.selectPermissions);

  const session = sessionSync.readSession();
  const activeToken = token() ?? session.token;
  const activeLegacyRole = legacyRole() ?? session.role;
  const profile = {
    userType: userType() ?? session.userType,
    roles: roles().length ? roles() : session.roles,
    permissions: permissions().length ? permissions() : session.permissions,
  };

  if (activeToken && canAccessBackofficeProfile(profile, activeLegacyRole)) {
    if (!token()) {
      store.dispatch(AuthActions.initSuccess(session));
    }

    const routingRoles = activeLegacyRole ? [...profile.roles, activeLegacyRole] : profile.roles;
    return router.createUrlTree([getDefaultBackofficeRouteForRoles(routingRoles)]);
  }

  return true;
};
